/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React from "react";
import { Redirect, Switch, Route, useLocation } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { Layout } from "../_metronic/layout";
import BasePage from "./BasePage";
import { Logout, AuthPage } from "./modules/Auth";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import { NotificationsProvider, GlobalProvider } from "app/providers";

export function Routes() {
  const location = useLocation();
  const { isAuthorized } = useSelector(
    ({ auth }) => ({
      isAuthorized: auth.user != null,
    }),
    shallowEqual
  );

  if (!isAuthorized && !location.pathname.startsWith("/auth")) {
    /**
     * User is not authorized and is not already on an auth-related page.
     * Redirect them to the login page while preserving the current location as a referrer.
     *
     * There are two scenarios we need to handle:
     *
     * 1. Normal redirection:
     *    - The user attempts to access a protected route (e.g. `/coleta-dados`)
     *    - We redirect them to `/auth/login?referrer=%2Fcoleta-dados`
     *
     * 2. Logout redirection:
     *    - The user has just logged out and is redirected via the logout route
     *    - Logout URL may include its own `referrer` param like:
     *      `/logout?referrer=%2Fcoleta-dados`
     *    - In this case, we must extract the `referrer` from the query param
     *      and pass it along to the login URL properly encoded:
     *      `/auth/login?referrer=%2Fcoleta-dados`
     */

    // Default: use the current path and search string as the referrer
    let redirectTo = `/auth/login?referrer=${encodeURIComponent(
      location.pathname + location.search
    )}`;

    // Special case: coming from /logout and already carrying a referrer in query params
    if (location.pathname.startsWith("/logout")) {
      const params = new URLSearchParams(location.search);
      const referrer = params.get("referrer");
      if (referrer) {
        redirectTo = `/auth/login?referrer=${encodeURIComponent(referrer)}`;
      }
    }

    return <Redirect to={redirectTo} />;
  }

  return (
    <Switch>
      <Route path="/auth">
        <AuthPage />
      </Route>
      <Route path="/error" component={ErrorsPage} />
      <Route path="/logout" component={Logout} />
      <GlobalProvider>
        <NotificationsProvider>
          <Layout>
            <BasePage />
          </Layout>
        </NotificationsProvider>
      </GlobalProvider>
    </Switch>
  );
}
